import React, { useEffect, useState } from "react";
import { Sparkles, Trash2, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const CATS = ["Guide", "Listicle", "Weekly", "Evergreen", "News"];
const MODELS = [
  { id: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
  { id: "gemini-3.1-pro-preview", label: "Gemini 3.1 Pro" },
  { id: "gemini-3-flash-preview", label: "Gemini 3 Flash" },
];
const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#00E5FF]";

export default function ContentManager() {
  const [articles, setArticles] = useState([]);
  const [topic, setTopic] = useState("");
  const [category, setCategory] = useState("Guide");
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/articles").then((r) => setArticles(r.data));
  useEffect(() => { load(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    if (!topic.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/ai/generate", { topic, category, model });
      toast.success("The RiNo Ranger published a new article!");
      setTopic(""); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/articles/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const suggestions = ["Best hidden gems in RiNo", "A perfect weekend in the River North Art District", "Top 10 murals to photograph in RiNo", "Where to eat before a First Friday"];

  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-white mb-5">Content Studio</h2>

      <div className="border border-[#FF0055]/40 bg-gradient-to-br from-[#1a0410] to-[#141414] p-6 mb-8">
        <div className="flex items-center gap-2 text-[#FF0055] font-accent text-sm"><Sparkles size={16} /> The RiNo Ranger — AI Writer (Claude Sonnet 4.6)</div>
        <form onSubmit={generate} className="mt-4 flex flex-col sm:flex-row gap-3">
          <input value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="ai-topic" placeholder="Topic, e.g. Best patios in RiNo this summer" className={`flex-1 ${inputCls}`} />
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="ai-category" className={`${inputCls} sm:w-36`}>
            {CATS.map((c) => <option key={c} value={c} className="bg-[#141414]">{c}</option>)}
          </select>
          <select value={model} onChange={(e) => setModel(e.target.value)} data-testid="ai-model" className={`${inputCls} sm:w-44`}>
            {MODELS.map((m) => <option key={m.id} value={m.id} className="bg-[#141414]">{m.label}</option>)}
          </select>
          <Button type="submit" disabled={busy} data-testid="ai-generate-btn" className="bg-[#FF0055] hover:bg-[#ff2d74] rounded-none px-6">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {busy ? "Writing..." : "Generate"}
          </Button>
        </form>
        <div className="flex flex-wrap gap-2 mt-3">
          {suggestions.map((s) => <button key={s} onClick={() => setTopic(s)} className="text-xs text-neutral-400 border border-white/10 px-2 py-1 hover:text-white hover:border-[#00E5FF]">{s}</button>)}
        </div>
      </div>

      <h3 className="font-accent text-[#00E5FF] text-sm mb-3">Published ({articles.length})</h3>
      <div className="space-y-2">
        {articles.map((a) => (
          <div key={a.id} className="flex items-center gap-4 border border-white/10 bg-[#141414] p-3" data-testid={`article-row-${a.id}`}>
            <img src={a.image} alt="" className="w-16 h-16 object-cover flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-accent text-[10px] text-[#00E5FF]">{a.category}</span>
                {a.ai_generated && <span className="text-[9px] text-[#FF0055] border border-[#FF0055]/40 px-1.5">AI</span>}
              </div>
              <div className="text-white text-sm font-medium truncate">{a.title}</div>
              <div className="text-neutral-500 text-xs truncate">{a.excerpt}</div>
            </div>
            <button onClick={() => del(a.id)} data-testid={`delete-article-${a.id}`} className="text-neutral-500 hover:text-[#FF0055]"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
